import { Button, Heading3Med, Heading4Med, Icon } from '@hai/ui-react';
import { isNil } from 'ramda';
import React, { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { triggerEvent } from 'src/js/events';
import { useAdaptiveFetch } from 'src/js/hook/use-adaptive-fetch';
import { useStores } from 'src/js/hook/use-stores';
import { StreamViewModel } from 'src/js/pages/streaming/stream/stream-models';
import SidebarLayout from 'src/js/sidebar';
import { openNewWindow } from 'src/js/util/global-util';
import { fetchStreamStatistics, fetchStreams, resetStreamStatistics } from 'src/js/pages/streaming/stream/stream-store';
import { StreamStatistics } from './stream/stream-statistics';
import { streamStateToColor, streamStateToIcon } from './stream/stream-view-model-mapper';
import { useLocalStorage } from 'usehooks-ts';
import { constant } from 'src/js/util/constant';
import { createIntervalOptions } from 'src/js/helper/chart-helper';
import { DropdownMenu, DropdownOption } from 'src/js/component/base/dropdown-menu';

export type StatsPageParameters = {
  id?: string;
};

interface Props {
  sidebar?: boolean;
}

let connectionLost = false;
export const FullStatsPage: React.FunctionComponent<Props> = ({ sidebar = true }) => {
  const { t } = useTranslation();
  const { sessionStore } = useStores();

  const { id } = useParams<StatsPageParameters>();
  const [streamData, setStreamData] = useState<StreamViewModel>();
  const [isSRT, setIsSRT] = useState(false);

  useEffect(() => {
    fetchStreams(t);
  }, []);

  const [refreshInterval, setRefreshInterval] = useLocalStorage(
    `${constant.lStorage.refreshIntervalStats}:${sessionStore.username}`,
    constant.defaultRefreshInterval,
  );

  const intervalOptions: DropdownOption[] = createIntervalOptions(
    constant.defaultRefreshIntervals,
    undefined /*prefix*/,
    setRefreshInterval,
    t,
  );
  const intervalIndex = intervalOptions.findIndex((option) => option.value === refreshInterval);

  useAdaptiveFetch(
    () =>
      fetchStreamStatistics({ id: Number(id) }, t, setStreamData).then((res) => {
        if (res.ok) {
          if (connectionLost) {
            triggerEvent('connection-restored');
            connectionLost = false;
          }
          if (!isSRT && res.data.stats?.srt) {
            setIsSRT(true);
          } else if (isSRT && !res.data?.stats.srt) {
            setIsSRT(false);
          }
        } else if (res.problem === 'NETWORK_ERROR') {
          triggerEvent('connection-lost');
          connectionLost = true;
        }
      }),
    refreshInterval,
    [refreshInterval],
  );

  const reset = (_e: React.MouseEvent<HTMLButtonElement>) => {
    resetStreamStatistics(streamData, t, setStreamData);
  };

  const handleClickFullPage = () => {
    const url = window.location.pathname.replace('stats', 'stats-full');
    openNewWindow(url);
  };

  const onDownloadCSV = () => {
    const url = '/apis/streams/' + streamData.id + '/stats?type=srt';
    openNewWindow(url, '_blank');
  };

  const statusColor = streamStateToColor(streamData?.state);

  const statisticsContent = (): ReactNode => {
    return (
      <>
        <header className="header d-flex align-items-center mt-3 mb-3">
          <div className="d-flex align-items-center">
            <Heading4Med>{t('statistics.header.detailedStatistics')}</Heading4Med>
            {sidebar && (
              <Icon
                iconname="GoTo"
                size="sm3"
                className="hai-ml-3 cursor-pointer"
                color="haiui-blue-04"
                onClick={handleClickFullPage}
              ></Icon>
            )}
          </div>
          <div className="buttons ml-auto d-flex align-items-center">
            <div className="mr-3 mt-1 ml-4">
              <DropdownMenu
                style={{ width: '200px' }}
                label={t('statistics.options.refreshInterval')}
                options={intervalOptions}
                selectedIndex={intervalIndex}
              />
            </div>
            {!sessionStore.isUser() && (
              <Button variant="secondary" onClick={reset}>
                {t('statistics.button.resetStats')}
              </Button>
            )}

            {isSRT && (
              <Button variant="primary" className="hai-ml-4" onClick={onDownloadCSV}>
                {t('statistics.button.downloadCSV')}
              </Button>
            )}
          </div>
        </header>
        {!isNil(streamData) && (
          <>
            <div className="full-page-header">
              <div className="status-indicator" style={{ backgroundColor: statusColor }} />
              <Icon iconname={streamStateToIcon(streamData.stats?.state)} style={{ fill: statusColor }} size={'md'} />
              <Heading3Med className="header-title">{streamData.name}</Heading3Med>
            </div>
            <StreamStatistics refreshInterval={refreshInterval} model={streamData} fullpage={true} parent="FULLPAGE" />
          </>
        )}
      </>
    );
  };

  return (
    <>
      {sidebar ? (
        <SidebarLayout>
          <div className="mk-fullpage-stats mt-5">
            <Link className="go-back link mt-5" to="/streaming">
              {t('statistics.stream.goBack')}
            </Link>
            <div className="separator" />
            {statisticsContent()}
          </div>
        </SidebarLayout>
      ) : (
        <div className="hai-p-6">{statisticsContent()} </div>
      )}
    </>
  );
};
